// Remote Support "remote control", OWNER side. The support agent watches a
// live mirror of this screen and sends pointer input back; this module
// renders their virtual pointer and replays that input on THIS document —
// clicks, drags, scrolling and typing behave as if the agent's hand were on
// the owner's mouse and keyboard.
//
// React inputs ignore plain `el.value = x`, so typing goes through the
// native value setter + an `input` event (the standard trick the app's
// browser extension also uses).

function setNativeValue(el, value) {
  var proto = el instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype;
  var descriptor = Object.getOwnPropertyDescriptor(proto, 'value');
  if (descriptor && descriptor.set) descriptor.set.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

function isTextInput(el) {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) {
    var type = String(el.type || 'text').toLowerCase();
    return ['text', 'search', 'email', 'url', 'password', 'number', 'tel', 'date'].indexOf(type) >= 0;
  }
  return false;
}

function insertIntoInput(el, text) {
  var start = el.selectionStart != null ? el.selectionStart : String(el.value).length;
  var end = el.selectionEnd != null ? el.selectionEnd : start;
  var value = String(el.value);
  var next = value.slice(0, start) + text + value.slice(end);
  setNativeValue(el, next);
  try { el.setSelectionRange(start + text.length, start + text.length); } catch (_) {}
}

function backspaceInput(el) {
  var start = el.selectionStart != null ? el.selectionStart : String(el.value).length;
  var end = el.selectionEnd != null ? el.selectionEnd : start;
  var value = String(el.value);
  if (start === end && start > 0) start = start - 1;
  setNativeValue(el, value.slice(0, start) + value.slice(end));
  try { el.setSelectionRange(start, start); } catch (_) {}
}

function mouseEventInit(x, y, p) {
  return {
    bubbles: true,
    cancelable: true,
    composed: true,
    view: window,
    clientX: x,
    clientY: y,
    screenX: x,
    screenY: y,
    button: (p && p.button) || 0,
    buttons: (p && p.buttons) || 0,
    ctrlKey: !!(p && p.ctrlKey),
    metaKey: !!(p && p.metaKey),
    shiftKey: !!(p && p.shiftKey),
    altKey: !!(p && p.altKey)
  };
}

function elementAt(x, y) {
  return document.elementFromPoint(x, y) || document.body;
}

function ancestorsOf(el) {
  var chain = [];
  var node = el;
  while (node) {
    chain.push(node);
    node = node.parentElement;
  }
  return chain;
}

// Full over/out/enter/leave chain as the pointer crosses element boundaries.
// mouseover/mouseout bubble; mouseenter/mouseleave don't, so they're
// dispatched per node between the old and new element's common ancestor.
// Without this, nothing that reacts to hover (node previews, toolbars, menu
// buttons) would respond to the remote pointer.
function dispatchHoverTransition(from, to, x, y, p) {
  if (from === to) return;
  var init = mouseEventInit(x, y, p);
  if (from) {
    from.dispatchEvent(new MouseEvent('mouseout', Object.assign({}, init, { relatedTarget: to })));
    from.dispatchEvent(new PointerEvent('pointerout', Object.assign({}, init, { relatedTarget: to, pointerType: 'mouse', isPrimary: true })));
  }
  if (to) {
    to.dispatchEvent(new MouseEvent('mouseover', Object.assign({}, init, { relatedTarget: from })));
    to.dispatchEvent(new PointerEvent('pointerover', Object.assign({}, init, { relatedTarget: from, pointerType: 'mouse', isPrimary: true })));
  }

  var fromChain = from ? ancestorsOf(from) : [];
  var toChain = to ? ancestorsOf(to) : [];
  var shared = fromChain.filter(function(node) { return toChain.indexOf(node) >= 0; })[0] || null;

  fromChain.forEach(function(node) {
    if (node === shared) return;
    if (shared && toChain.indexOf(node) >= 0) return;
    node.dispatchEvent(new MouseEvent('mouseleave', Object.assign({}, init, { bubbles: false, relatedTarget: to })));
    node.dispatchEvent(new PointerEvent('pointerleave', Object.assign({}, init, { bubbles: false, relatedTarget: to, pointerType: 'mouse', isPrimary: true })));
  });
  // Outermost-first, the order a real pointer entry produces.
  toChain.slice().reverse().forEach(function(node) {
    if (node === shared) return;
    if (shared && fromChain.indexOf(node) >= 0) return;
    node.dispatchEvent(new MouseEvent('mouseenter', Object.assign({}, init, { bubbles: false, relatedTarget: from })));
    node.dispatchEvent(new PointerEvent('pointerenter', Object.assign({}, init, { bubbles: false, relatedTarget: from, pointerType: 'mouse', isPrimary: true })));
  });
}

// Put the text caret exactly where the remote pointer clicked, so typing
// lands at the click position instead of the end of the field.
function placeCaretAt(el, x, y) {
  if (isTextInput(el)) {
    var offset = null;
    if (document.caretPositionFromPoint) {
      var pos = document.caretPositionFromPoint(x, y);
      if (pos) offset = pos.offset;
    } else if (document.caretRangeFromPoint) {
      var range = document.caretRangeFromPoint(x, y);
      if (range) offset = range.startOffset;
    }
    if (offset == null) return;
    try { el.setSelectionRange(offset, offset); } catch (_) {}
    return;
  }
  if (el && el.isContentEditable) {
    var editRange = document.caretRangeFromPoint
      ? document.caretRangeFromPoint(x, y)
      : null;
    if (!editRange && document.caretPositionFromPoint) {
      var cp = document.caretPositionFromPoint(x, y);
      if (cp) {
        editRange = document.createRange();
        editRange.setStart(cp.offsetNode, cp.offset);
      }
    }
    if (!editRange) return;
    editRange.collapse(true);
    var selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(editRange);
  }
}

// Walk up from the wheel target to the nearest scrollable box.
function scrollTargetFor(el) {
  var node = el;
  while (node && node !== document.body && node !== document.documentElement) {
    var style;
    try { style = window.getComputedStyle(node); } catch (_) { break; }
    var overflowY = style ? style.overflowY : '';
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// One virtual pointer per connected agent.
function createRemotePointer(identity) {
  var color = (identity && identity.color) || '#7c5cff';
  var username = (identity && identity.username) || 'Support';

  var el = document.createElement('div');
  el.className = 'remote-virtual-pointer';
  el.innerHTML =
    '<svg width="18" height="18" viewBox="0 0 24 24">' +
    '<path d="M4 2l16 8.5-7 1.8L9.5 20 4 2z" fill="' + color + '" stroke="#ffffff" stroke-width="1.4"></path>' +
    '</svg>' +
    '<span class="remote-virtual-pointer-name" style="background:' + color + '"></span>';
  el.querySelector('.remote-virtual-pointer-name').textContent = username;
  document.body.appendChild(el);

  var hoverTarget = null;

  function move(x, y, p) {
    el.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
    var target = elementAt(x, y);
    dispatchHoverTransition(hoverTarget, target, x, y, p);
    hoverTarget = target;
    var init = mouseEventInit(x, y, p);
    // Node drags listen on window, so these must bubble from the real target.
    target.dispatchEvent(new PointerEvent('pointermove', Object.assign({}, init, { pointerType: 'mouse', isPrimary: true })));
    target.dispatchEvent(new MouseEvent('mousemove', init));
  }

  function clearHover(x, y, p) {
    if (!hoverTarget) return;
    dispatchHoverTransition(hoverTarget, null, x || 0, y || 0, p);
    hoverTarget = null;
  }

  function handle(p) {
    if (!p || !p.kind) return;
    if (p.kind === 'move') return move(p.x, p.y, p);
    if (p.kind === 'leave') return clearHover(p.x, p.y, p);
    if (p.kind === 'down') {
      move(p.x, p.y, p);
      var downTarget = elementAt(p.x, p.y);
      var downInit = mouseEventInit(p.x, p.y, p);
      downTarget.dispatchEvent(new PointerEvent('pointerdown', Object.assign({}, downInit, { pointerType: 'mouse', isPrimary: true })));
      downTarget.dispatchEvent(new MouseEvent('mousedown', downInit));
      return;
    }
    if (p.kind === 'up') {
      var upTarget = elementAt(p.x, p.y);
      var upInit = mouseEventInit(p.x, p.y, p);
      upTarget.dispatchEvent(new PointerEvent('pointerup', Object.assign({}, upInit, { pointerType: 'mouse', isPrimary: true })));
      upTarget.dispatchEvent(new MouseEvent('mouseup', upInit));
      return;
    }
    if (p.kind === 'contextmenu') {
      elementAt(p.x, p.y).dispatchEvent(new MouseEvent('contextmenu', mouseEventInit(p.x, p.y, p)));
      return;
    }
    if (p.kind === 'click' || p.kind === 'dblclick') {
      var target = elementAt(p.x, p.y);
      // Land focus like a real click would, so subsequent typing goes to
      // the field the agent clicked — and drop the caret at the click point.
      var focusable = target && typeof target.closest === 'function'
        ? target.closest('input, textarea, select, button, [contenteditable="true"], [tabindex]')
        : null;
      if (focusable && typeof focusable.focus === 'function') {
        try { focusable.focus({ preventScroll: true }); } catch (_) { try { focusable.focus(); } catch (_e) {} }
        placeCaretAt(focusable, p.x, p.y);
      } else if (document.activeElement && document.activeElement !== document.body) {
        try { document.activeElement.blur(); } catch (_) {}
      }
      target.dispatchEvent(new MouseEvent(p.kind, mouseEventInit(p.x, p.y, p)));
      return;
    }
    if (p.kind === 'wheel') {
      var wheelTarget = elementAt(p.x, p.y);
      var wheelEvent = new WheelEvent('wheel', Object.assign(mouseEventInit(p.x, p.y), {
        deltaX: p.deltaX || 0,
        deltaY: p.deltaY || 0,
        deltaMode: 0
      }));
      wheelTarget.dispatchEvent(wheelEvent);
      if (!wheelEvent.defaultPrevented) {
        var scroller = scrollTargetFor(wheelTarget);
        if (scroller) scroller.scrollBy({ left: p.deltaX || 0, top: p.deltaY || 0 });
        else window.scrollBy({ left: p.deltaX || 0, top: p.deltaY || 0 });
      }
      return;
    }
    if (p.kind === 'key') return handleKey(p);
  }

  function handleKey(p) {
    var active = document.activeElement;
    var keydown = new KeyboardEvent('keydown', {
      key: p.key,
      code: p.code || '',
      bubbles: true,
      cancelable: true,
      ctrlKey: !!p.ctrlKey,
      metaKey: !!p.metaKey,
      shiftKey: !!p.shiftKey,
      altKey: !!p.altKey
    });
    var dispatchTarget = active && active !== document.body ? active : document;
    dispatchTarget.dispatchEvent(keydown);
    // The app handled it (e.g. Enter submitted a chat) — don't also insert.
    if (keydown.defaultPrevented) return;
    if (p.ctrlKey || p.metaKey) return;

    var printable = typeof p.key === 'string' && p.key.length === 1;
    var editable = active && active.isContentEditable;

    if (printable) {
      if (isTextInput(active)) insertIntoInput(active, p.key);
      else if (editable) document.execCommand('insertText', false, p.key);
    } else if (p.key === 'Backspace') {
      if (isTextInput(active)) backspaceInput(active);
      else if (editable) document.execCommand('delete');
    } else if (p.key === 'Enter') {
      if (active instanceof HTMLTextAreaElement) insertIntoInput(active, '\n');
      else if (editable) document.execCommand('insertText', false, '\n');
    }
    dispatchTarget.dispatchEvent(new KeyboardEvent('keyup', {
      key: p.key, code: p.code || '', bubbles: true,
      ctrlKey: !!p.ctrlKey, metaKey: !!p.metaKey, shiftKey: !!p.shiftKey, altKey: !!p.altKey
    }));
  }

  return {
    handle: handle,
    destroy: function() {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    }
  };
}

module.exports = { createRemotePointer };
